<template>
  <div class="exam-status">
    <HDFHeader03 />

    <div class="page-wrapper">
      <el-container>
        <el-header class="demo-header">
          <h1>考试报名状态</h1>
        </el-header>
        
        <el-main>
          <el-row>
            <el-col :span="4"></el-col>
            <el-col :span="16">
              <el-card class="status-card">
                <template #header>
                  <div class="card-header">
                    <span>报名信息详情</span>
                  </div>
                </template>
                
                <div class="status-content">
                  <el-result 
                    icon="success" 
                    :title="'报名信息已提交（报名费：' + (examInfo.payState == 1 ? '已支付' : '未支付') + '）'" 
                    sub-title="您的考试报名信息已成功提交，请等待审核">
                    <template #extra>
                      <el-button type="primary" @click="$router.push('/exam-list')">返回报名页</el-button>
                      <el-button type="primary" @click="showPayDialog(examInfo)" v-if="examInfo.payState == 0">点击支付报名费</el-button>
                    </template>
                  </el-result>
                  
                  <el-divider />
                  
                  <el-descriptions 
                    class="margin-top" 
                    title="报名信息详情" 
                    :column="2" 
                    border>
                    <el-descriptions-item label="证件照片">
                      <el-image
                        style="width: 100px; height: 100px"
                        :src="examInfo.img"
                        :preview-src-list="[examInfo.img]"
                        :hide-on-click-modal="true"
                        fit="cover"
                      />
                    </el-descriptions-item>
                    <el-descriptions-item label="考试信息">
                      {{ examInfo.sessionTitle }}
                    </el-descriptions-item>
                    <el-descriptions-item label="考试类型">
                      {{ examInfo.type }}
                    </el-descriptions-item>
                    <el-descriptions-item label="考试等级">
                      {{ examInfo.level }}
                    </el-descriptions-item>
                    <el-descriptions-item label="编程语言">
                      {{ examInfo.lang }}
                    </el-descriptions-item>
                    <el-descriptions-item label="培训机构">
                      {{ getGroupText(examInfo.groupId) }}
                    </el-descriptions-item>
                    <el-descriptions-item label="考试期次">
                      {{ examInfo.areaName }}
                    </el-descriptions-item>
                    <el-descriptions-item label="地区">
                      {{ examInfo.province }} {{ examInfo.city }}
                    </el-descriptions-item>
                    <el-descriptions-item label="考生姓名">
                      {{ examInfo.name }}
                    </el-descriptions-item>
                    <el-descriptions-item label="性别">
                      {{ examInfo.sex }}
                    </el-descriptions-item>
                    <el-descriptions-item label="手机号码">
                      {{ examInfo.telephone }}
                    </el-descriptions-item>
                    <el-descriptions-item label="证件类型">
                      {{ getIdentityTypeText(examInfo.identityType) }}
                    </el-descriptions-item>
                    <el-descriptions-item label="证件号码">
                      {{ examInfo.identity }}
                    </el-descriptions-item>
                    <el-descriptions-item label="证书获取方式">
                      {{ getPostWayText(examInfo.postWay) }}
                    </el-descriptions-item>
                    <el-descriptions-item label="证书邮寄地址" :span="2">
                      {{ examInfo.postAddress }}
                    </el-descriptions-item>
                    <el-descriptions-item label="学校">
                      {{ examInfo.schoolName || '无' }}
                    </el-descriptions-item>
                    <el-descriptions-item label="参加的培训班">
                      {{ examInfo.trainingInstitution }}
                    </el-descriptions-item>
                    <el-descriptions-item label="紧急联系人">
                      {{ examInfo.emergencyContactName }}
                    </el-descriptions-item>
                    <el-descriptions-item label="紧急联系人电话">
                      {{ examInfo.emergencyContactPhone }}
                    </el-descriptions-item>
                    <el-descriptions-item label="发票信息">
                      {{ examInfo.bill === '不开' ? '不需要' : '个人发票' }}
                    </el-descriptions-item>
                    <el-descriptions-item label="电子邮箱" v-if="examInfo.bill === '个人'">
                      {{ examInfo.billAddress }}
                    </el-descriptions-item>
                  </el-descriptions>
                  
                  <div class="status-footer">
                    <el-alert
                      title="注意事项"
                      description="您的报名信息已提交成功，工作人员会在3个工作日内完成审核。审核通过后，您将收到短信通知，请保持手机畅通。准考证将在考试前一周开放打印，请及时关注。"
                      type="info"
                      show-icon
                      :closable="false"
                    />
                  </div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="4"></el-col>
          </el-row>
        </el-main>
      </el-container>
    </div>

    <HDFFooter03 />

    <PaymentModal ref="paymentModelRef"/>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { apiGetExamInfoById, apiCreateOrder } from '@/api/exam'
import { getWxPayInfo, getAliPayInfo } from '@/api/pay'

import HDFHeader03 from '@/components/HDFHeader03.vue'
import HDFFooter03 from '@/components/HDFFooter03.vue'
import PaymentModal from '@/components/PaymentModal.vue'

const paymentModelRef = ref(null)

// 模拟已提交的考试信息
const examInfo = ref({
  img: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80',
  sessionTitle: '',
  level: 1,
  lang: '',
  groupId: 0,
  areaName: '',
  province: '',
  city: '',
  name: '',
  sex: '',
  telphone: '',
  identityType: 1,
  identity: '',
  postWay: '',
  postAddress: '',
  schoolName: '',
  trainingInstitution: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  bill: '',
  billAddress: ''
})

const queryParams = ref({});

const getGroupText = (groupId) => {
  const groupMap = {
    4: '童程童美',
    6: '小码精灵',
    7: '学而思',
    8: '测试模拟',
    9: '核桃编程',
    '-2': '其它培训机构',
    '-4': '未参加培训机构'
  }
  return groupMap[groupId] || '未知'
}

const getIdentityTypeText = (type) => {
  const typeMap = {
    1: '大陆身份证',
    2: '香港身份证',
    3: '澳门身份证',
    4: '台湾身份证',
    5: '护照',
    6: '港澳通行证',
    7: '其他'
  }
  return typeMap[type] || '未知'
}

const getPostWayText = (way) => {
  const wayMap = {
    1: '由机构老师代领',
    2: '快递',
    3: '不要纸质证书'
  }
  return wayMap[way] || '未知'
}

const getQueryParams = () => {
  const query = {}
  const queryString = window.location.search.substring(1)
  const queryArray = queryString.split('&')
  for (let i = 0; i < queryArray.length; i++) {
    const queryItem = queryArray[i].split('=')
    query[queryItem[0]] = queryItem[1]
  }
  return query
}

const showPayDialog = (data) => {
  createOrder(data.id, (orderInfo) => {
    console.log(orderInfo)
    paymentModelRef.value.showDialog(
      '选择支付方式',
      orderInfo.orderNo,
      orderInfo.price,
      '考试报名费用'
    )
  })
}

const createOrder = (examId, callback) => {
  apiCreateOrder({examId}).then(response => { 
    const res = response.data;
    if (res.code === 200) {
      callback?.(res.data);
    }
  })
}

onMounted(() => {
  queryParams.value = getQueryParams()

  // 获取考试信息
  apiGetExamInfoById({ sessionId: queryParams.value.id }).then((response) => {
    const res = response.data
    if (res.code === 200) {
      examInfo.value = res.data
    }
  })
})

</script>

<style scoped>
.exam-status {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.page-wrapper {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.demo-header h1 {
  text-align: center;
  margin: 20px 0;
}

.status-card {
  margin-bottom: 20px;
}

.status-content {
  padding: 20px;
}

.status-footer {
  margin-top: 30px;
}

@media (max-width: 768px) {
  .page-wrapper {
    padding: 0 10px;
  }
}
</style>